import { useCallback, useEffect, useMemo, useRef, useState, memo } from "react";
import { Search, X, ArrowDown } from "lucide-react";
import { sectionRegistry, type SectionType } from "@sections/registry";
import { RenderContextProvider } from "@sections/RenderContext";

// ---------------------------------------------------------------------------
// Library data
// ---------------------------------------------------------------------------

interface LibraryVariant {
  name: string;
  description: string;
  /** Section registry type to instantiate */
  sectionType: SectionType;
  /** Default props passed to addSection */
  defaultProps: Record<string, unknown>;
  /** Optional overrides applied ONLY to the thumbnail preview, not inserted props */
  thumbnailProps?: Record<string, unknown>;
}

interface LibraryCategory {
  cat: string;
  variants: LibraryVariant[];
}

/**
 * Master library catalog. Only variants whose `sectionType` exists in the
 * registry are shown. When a new renderer lands, its variants appear here
 * automatically.
 */
const FULL_LIBRARY: LibraryCategory[] = [
  {
    cat: "Heroes",
    variants: [
      {
        name: "Centered hero",
        description: "Headline, subtext, one button",
        sectionType: "Hero",
        defaultProps: { layout: "centered", alignment: "center" },
        thumbnailProps: { backgroundStyle: "primary" },
      },
      {
        name: "Left-aligned hero",
        description: "Copy left, breathing room right",
        sectionType: "Hero",
        defaultProps: { layout: "left", alignment: "left" },
      },
      {
        name: "Split hero",
        description: "Text on one side, image on the other",
        sectionType: "Hero",
        defaultProps: {
          layout: "split",
          heading: "Expert Guidance for Every Stage of Your Business",
          subheading: "We combine deep expertise with a personal approach to help you achieve your goals.",
          ctaLabel: "Get Started",
          ctaHref: "/book",
          imagePosition: "right",
        },
        thumbnailProps: { backgroundStyle: "primaryBg" },
      },
      {
        name: "Image hero",
        description: "Full-bleed photo with dark overlay",
        sectionType: "Hero",
        defaultProps: {
          layout: "image",
          heading: "Your Success Is Our Mission",
          subheading: "Trusted professionals delivering results that matter.",
          ctaLabel: "Schedule a Consultation",
          ctaHref: "/book",
        },
      },
      {
        name: "Slim banner",
        description: "Short announcement strip",
        sectionType: "Hero",
        defaultProps: {
          layout: "banner",
          backgroundStyle: "primary",
          heading: "Now accepting new clients -- schedule your free consultation today.",
          ctaLabel: "Book Now",
          ctaHref: "/book",
        },
      },
    ],
  },
  {
    cat: "Features",
    variants: [
      {
        name: "2-column grid",
        description: "Two cards side by side",
        sectionType: "FeatureGrid",
        defaultProps: { columns: 2 },
        thumbnailProps: { backgroundStyle: "primaryBg" },
      },
      {
        name: "3-column grid",
        description: "Classic three-up",
        sectionType: "FeatureGrid",
        defaultProps: { columns: 3 },
      },
      {
        name: "4-column grid",
        description: "Compact four-up",
        sectionType: "FeatureGrid",
        defaultProps: { columns: 4 },
        thumbnailProps: { backgroundStyle: "secondaryBg" },
      },
      {
        name: "Icon List",
        description: "Numbered vertical feature list",
        sectionType: "FeatureList",
        defaultProps: {},
      },
      {
        name: "Feature Showcase",
        description: "Alternating image + text rows",
        sectionType: "FeatureShowcase",
        defaultProps: {},
        thumbnailProps: { backgroundStyle: "surface" },
      },
    ],
  },
  {
    cat: "Content",
    variants: [
      {
        name: "Rich Text",
        description: "Formatted paragraph content",
        sectionType: "TextBlock",
        defaultProps: { layout: "prose" },
      },
      {
        name: "Heading + Text",
        description: "Prominent heading with body copy",
        sectionType: "TextBlock",
        defaultProps: { layout: "heading-text" },
        thumbnailProps: { backgroundStyle: "surface" },
      },
      {
        name: "Two-Column Text",
        description: "Side-by-side text columns",
        sectionType: "TextBlock",
        defaultProps: { layout: "two-column" },
        thumbnailProps: { backgroundStyle: "secondaryBg" },
      },
      {
        name: "Pull Quote",
        description: "Large, impactful statement",
        sectionType: "TextBlock",
        defaultProps: { layout: "pull-quote" },
        thumbnailProps: { backgroundStyle: "primary" },
      },
      {
        name: "Divider",
        description: "Visual separator between sections",
        sectionType: "TextBlock",
        defaultProps: { layout: "divider" },
      },
      {
        name: "Image + Text",
        description: "Side-by-side image and text",
        sectionType: "ImageText",
        defaultProps: {},
        thumbnailProps: { backgroundStyle: "surface" },
      },
    ],
  },
  {
    cat: "Pricing",
    variants: [
      {
        name: "2-panel pricing",
        description: "Two plans side by side",
        sectionType: "PricingTable",
        defaultProps: { layout: "panels" },
      },
      {
        name: "3-panel pricing",
        description: "Good / better / best",
        sectionType: "PricingTable",
        defaultProps: { layout: "panels" },
        thumbnailProps: { backgroundStyle: "surface" },
      },
      {
        name: "Rate Table",
        description: "Line-item services with prices",
        sectionType: "PricingTable",
        defaultProps: { layout: "rate-table" },
        thumbnailProps: { backgroundStyle: "secondaryBg" },
      },
    ],
  },
  {
    cat: "Testimonials",
    variants: [
      {
        name: "Single Testimonial",
        description: "One large, prominent quote",
        sectionType: "Testimonials",
        defaultProps: {
          layout: "single",
          items: [
            {
              quote: "Working with this team transformed how we approach our business. Their expertise and dedication exceeded every expectation.",
              author: "Sarah M.",
              role: "Business Owner",
            },
          ],
        },
        thumbnailProps: { backgroundStyle: "secondaryBg" },
      },
      {
        name: "Testimonial Grid",
        description: "Three quotes in a grid",
        sectionType: "Testimonials",
        defaultProps: { layout: "grid" },
        thumbnailProps: { backgroundStyle: "primaryBg" },
      },
      {
        name: "Testimonial Carousel",
        description: "Swipeable quote cards",
        sectionType: "Testimonials",
        defaultProps: { layout: "carousel" },
      },
    ],
  },
  {
    cat: "FAQ",
    variants: [
      {
        name: "Accordion",
        description: "Expandable questions",
        sectionType: "FAQ",
        defaultProps: { layout: "accordion" },
      },
      {
        name: "Two-Column FAQ",
        description: "Question left, answer right",
        sectionType: "FAQ",
        defaultProps: { layout: "two-column" },
        thumbnailProps: { backgroundStyle: "secondaryBg" },
      },
    ],
  },
  {
    cat: "Calls to action",
    variants: [
      {
        name: "Color Band CTA",
        description: "Full-width call to action",
        sectionType: "CTABanner",
        defaultProps: { layout: "band" },
      },
      {
        name: "Split CTA",
        description: "Text left, button right",
        sectionType: "CTABanner",
        defaultProps: {
          layout: "split",
          heading: "Let's Work Together",
          body: "Schedule a conversation with our team to discuss how we can help.",
          ctaLabel: "Book a Call",
          ctaHref: "/book",
          backgroundStyle: "primary",
        },
        thumbnailProps: { backgroundStyle: "primary" },
      },
      {
        name: "Boxed Card CTA",
        description: "Floating card with shadow",
        sectionType: "CTABanner",
        defaultProps: {
          layout: "boxed",
          heading: "Ready to Take the Next Step?",
          body: "Our team is here to answer your questions and help you move forward with confidence.",
          ctaLabel: "Get Started",
          ctaHref: "/book",
          backgroundStyle: "surface",
        },
        thumbnailProps: { backgroundStyle: "surface" },
      },
      {
        name: "CTA Cards",
        description: "Grid of action cards with buttons",
        sectionType: "CTACards",
        defaultProps: {},
      },
    ],
  },
  {
    cat: "Stats & Numbers",
    variants: [
      {
        name: "Stats Bar",
        description: "Key statistics in a row",
        sectionType: "StatsBar",
        defaultProps: {},
      },
    ],
  },
  {
    cat: "Booking",
    variants: [
      {
        name: "Booking Widget",
        description: "Booking prompt with feature highlights",
        sectionType: "BookingWidget",
        defaultProps: {},
      },
    ],
  },
  {
    cat: "Blog",
    variants: [
      {
        name: "Latest 3 Posts",
        description: "Newest articles in a grid",
        sectionType: "BlogGrid",
        defaultProps: { layout: "grid" },
      },
      {
        name: "Featured + List",
        description: "Hero post with compact list below",
        sectionType: "BlogGrid",
        defaultProps: { layout: "featured" },
        thumbnailProps: { backgroundStyle: "secondaryBg" },
      },
      {
        name: "Blog Listing (Grid)",
        description: "Paginated grid with tag filtering",
        sectionType: "BlogList",
        defaultProps: { layout: "grid", showTagFilter: true, showPagination: true },
      },
      {
        name: "Blog Listing (List)",
        description: "Paginated list with tag filtering",
        sectionType: "BlogList",
        defaultProps: { layout: "list", showTagFilter: true, showPagination: true },
        thumbnailProps: { backgroundStyle: "surface" },
      },
      {
        name: "Blog Listing (Editorial)",
        description: "Hero + trio + mosaic pattern rows",
        sectionType: "BlogList",
        defaultProps: { layout: "editorial", showTagFilter: true, showPagination: true },
      },
    ],
  },
  {
    cat: "Process & Steps",
    variants: [
      {
        name: "Process Steps",
        description: "Numbered steps connected by lines",
        sectionType: "ProcessSteps",
        defaultProps: { layout: "steps" },
        thumbnailProps: { backgroundStyle: "surface" },
      },
      {
        name: "Timeline",
        description: "Vertical timeline with milestones",
        sectionType: "ProcessSteps",
        defaultProps: { layout: "timeline" },
      },
    ],
  },
  {
    cat: "Contact",
    variants: [
      {
        name: "Contact Card",
        description: "Phone, email, hours, and address",
        sectionType: "ContactCard",
        defaultProps: { layout: "list" },
      },
      {
        name: "Contact Grid",
        description: "Contact info in a 2x2 card grid",
        sectionType: "ContactCard",
        defaultProps: { layout: "grid" },
        thumbnailProps: { backgroundStyle: "surface" },
      },
      {
        name: "Lead Form",
        description: "Enquiry form that creates a lead",
        sectionType: "LeadForm",
        defaultProps: { layout: "stacked" },
      },
      {
        name: "Lead Form (Two Column)",
        description: "Side-by-side enquiry form layout",
        sectionType: "LeadForm",
        defaultProps: { layout: "two-column" },
      },
    ],
  },
  {
    cat: "Team",
    variants: [
      {
        name: "Team Grid",
        description: "Team member profiles in a grid",
        sectionType: "TeamGrid",
        defaultProps: { layout: "grid" },
      },
      {
        name: "Team Featured",
        description: "Lead member large, rest in grid",
        sectionType: "TeamGrid",
        defaultProps: { layout: "featured" },
        thumbnailProps: { backgroundStyle: "primaryBg" },
      },
    ],
  },
  {
    cat: "Comparison",
    variants: [
      {
        name: "Comparison Table",
        description: "Feature comparison with check marks",
        sectionType: "ComparisonTable",
        defaultProps: {},
      },
    ],
  },
  {
    cat: "Logo Strips",
    variants: [
      {
        name: "Logo Strip",
        description: "Partner logos in a row",
        sectionType: "LogoStrip",
        defaultProps: {},
        thumbnailProps: { backgroundStyle: "surface" },
      },
    ],
  },
  {
    cat: "Media & Embeds",
    variants: [
      {
        name: "Video Embed",
        description: "YouTube or Vimeo video",
        sectionType: "VideoEmbed",
        defaultProps: {},
      },
      {
        name: "Map Embed",
        description: "Office location map",
        sectionType: "MapEmbed",
        defaultProps: {},
        thumbnailProps: { backgroundStyle: "surface" },
      },
      {
        name: "Custom Embed",
        description: "Any allowed widget by URL",
        sectionType: "CustomEmbed",
        defaultProps: {},
      },
    ],
  },
];

/**
 * Filter the catalog to only categories/variants whose section type
 * actually has a renderer in the registry.
 */
function useAvailableLibrary(): LibraryCategory[] {
  return useMemo(() => {
    const available: LibraryCategory[] = [];
    for (const cat of FULL_LIBRARY) {
      const vs = cat.variants.filter(
        (v) => v.sectionType in sectionRegistry,
      );
      if (vs.length > 0) {
        available.push({ ...cat, variants: vs });
      }
    }
    return available;
  }, []);
}

// ---------------------------------------------------------------------------
// Live component thumbnails
// ---------------------------------------------------------------------------

const THUMBNAIL_RENDER_CONTEXT = {
  blogPosts: [] as never[],
  faqItems: [
    { id: "1", question: "What services do you offer?", answer: "We offer a range of professional services." },
    { id: "2", question: "How do I get started?", answer: "Contact us to schedule a consultation." },
    { id: "3", question: "What are your rates?", answer: "Our rates vary by service type." },
  ],
  bookingTypes: [
    { id: "1", name: "Consultation", description: "One-on-one session", sortOrder: 1, pricingPlan: { name: "Standard", authAmount: 100, hourlyRate: 150 } },
    { id: "2", name: "Group Session", description: "Small group format", sortOrder: 2, pricingPlan: { name: "Group", authAmount: 50, hourlyRate: 75 } },
    { id: "3", name: "Premium", description: "Extended session", sortOrder: 3, pricingPlan: { name: "Premium", authAmount: 200, hourlyRate: 300 } },
  ],
  isEditor: true,
};

/**
 * Renders the real section component at full width inside a scaled-down,
 * non-interactive container. The component renders at 1000px width, then
 * CSS transform scales it to fit the thumbnail area (~130px visible).
 */
const LiveThumb = memo(function LiveThumb({
  sectionType,
  defaultProps,
  thumbnailProps,
}: {
  sectionType: SectionType;
  defaultProps: Record<string, unknown>;
  thumbnailProps?: Record<string, unknown>;
}) {
  const mod = sectionRegistry[sectionType];
  if (!mod) return null;

  const mergedProps = { ...mod.defaults, ...defaultProps, ...thumbnailProps };
  const SectionComponent = mod.Component;

  return (
    <span className="block h-[54px] rounded-lg overflow-hidden relative border border-paper-2 bg-paper-hi">
      <span
        className="block pointer-events-none select-none"
        style={{
          width: 1000,
          transformOrigin: "top left",
          transform: "scale(0.13)",
        }}
      >
        <RenderContextProvider value={THUMBNAIL_RENDER_CONTEXT}>
          <span className="@container block">
            <SectionComponent {...(mergedProps as any)} />
          </span>
        </RenderContextProvider>
      </span>
    </span>
  );
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface SectionLibraryProps {
  open: boolean;
  onClose: () => void;
  onInsert: (type: SectionType, defaultProps: Record<string, unknown>) => void;
  /** Human-readable label for where the new section will be inserted. */
  insertAfterLabel?: string;
}

export function SectionLibrary({
  open,
  onClose,
  onInsert,
  insertAfterLabel,
}: SectionLibraryProps) {
  const library = useAvailableLibrary();
  const [activeCat, setActiveCat] = useState("All");
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const modalRef = useRef<HTMLDivElement>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setActiveCat("All");
      setSearch("");
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [open]);

  // Scroll lock: prevent body scroll while library is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Focus trap: cycle tab through modal controls only
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== "Tab") return;
    const container = modalRef.current;
    if (!container) return;
    const focusable = container.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  const totalCount = useMemo(
    () => library.reduce((s, c) => s + c.variants.length, 0),
    [library],
  );

  // Filter variants by active category and search query
  const filteredCategories = useMemo(() => {
    const q = search.toLowerCase();
    return library
      .filter((c) => activeCat === "All" || c.cat === activeCat)
      .map((c) => ({
        ...c,
        variants: c.variants.filter(
          (v) =>
            !q ||
            v.name.toLowerCase().includes(q) ||
            v.description.toLowerCase().includes(q),
        ),
      }))
      .filter((c) => c.variants.length > 0);
  }, [library, activeCat, search]);

  const handleInsert = useCallback(
    (variant: LibraryVariant) => {
      onInsert(variant.sectionType, { ...variant.defaultProps });
    },
    [onInsert],
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-center animate-[fadein_0.15s_ease]"
      role="dialog"
      aria-modal="true"
      aria-label="Section library"
    >
      {/* Scrim */}
      <div
        className="absolute inset-0 bg-ink/40 backdrop-blur-[3px]"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        ref={modalRef}
        onKeyDown={handleKeyDown}
        className="relative w-[860px] max-w-[calc(100vw-48px)] h-[620px] max-h-[calc(100vh-60px)] bg-paper-hi rounded-[18px] shadow-[0_30px_80px_-20px_rgba(11,37,69,0.55)] flex flex-col overflow-hidden animate-[rise_0.2s_cubic-bezier(0.3,1.2,0.4,1)]"
      >
        {/* Header */}
        <div className="flex items-baseline gap-3.5 px-[26px] pt-[22px] pb-3.5 border-b border-paper-2">
          <h2 className="font-display text-[22px] font-semibold text-ink">
            Section library
          </h2>
          <span className="text-[12.5px] text-ink-4">
            Every section automatically matches your brand.
          </span>
          <div className="ml-auto relative">
            <Search
              size={13}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-5 pointer-events-none"
            />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search sections..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-[220px] border border-ink-6 rounded-full bg-paper pl-9 pr-3.5 py-[7px] text-[12.5px] font-sans text-ink placeholder:text-ink-5 focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-shadow"
            />
          </div>
          <button
            onClick={onClose}
            className="ml-2 w-[28px] h-[28px] rounded-lg grid place-items-center text-ink-4 hover:bg-paper-2 hover:text-ink-2 transition-colors"
            title="Close"
          >
            <X size={15} strokeWidth={2} />
          </button>
        </div>

        {/* Insertion-point indicator */}
        {insertAfterLabel && (
          <div className="flex items-center gap-2 px-[26px] py-2 bg-tide-ss border-b border-paper-2 text-[12px] text-tide-d">
            <ArrowDown size={12} strokeWidth={2.5} />
            <span>
              Inserting{" "}
              {insertAfterLabel.replace(
                /\b([A-Z])([A-Z]+)\b/g,
                (_, first: string, rest: string) =>
                  first + rest.toLowerCase(),
              )}
            </span>
          </div>
        )}

        {/* Body */}
        <div className="flex flex-1 min-h-0">
          {/* Category rail */}
          <nav className="w-[168px] flex-none border-r border-paper-2 py-3.5 px-2.5 overflow-y-auto bg-paper">
            <button
              onClick={() => setActiveCat("All")}
              className={`flex items-center w-full border-0 bg-transparent text-left text-[12.5px] font-medium px-2.5 py-[7px] rounded-lg gap-2 transition-colors ${
                activeCat === "All"
                  ? "bg-tide-ss text-tide-d font-semibold"
                  : "text-ink-3 hover:bg-paper-2"
              }`}
            >
              All sections
              <span
                className={`ml-auto text-[10px] font-bold rounded-full px-[7px] py-px ${
                  activeCat === "All"
                    ? "bg-tide-s text-tide-d"
                    : "bg-canvas-2 text-ink-5"
                }`}
              >
                {totalCount}
              </span>
            </button>
            {library.map((cat) => (
              <button
                key={cat.cat}
                onClick={() => setActiveCat(cat.cat)}
                className={`flex items-center w-full border-0 bg-transparent text-left text-[12.5px] font-medium px-2.5 py-[7px] rounded-lg gap-2 transition-colors ${
                  activeCat === cat.cat
                    ? "bg-tide-ss text-tide-d font-semibold"
                    : "text-ink-3 hover:bg-paper-2"
                }`}
              >
                {cat.cat}
                <span
                  className={`ml-auto text-[10px] font-bold rounded-full px-[7px] py-px ${
                    activeCat === cat.cat
                      ? "bg-tide-s text-tide-d"
                      : "bg-canvas-2 text-ink-5"
                  }`}
                >
                  {cat.variants.length}
                </span>
              </button>
            ))}
          </nav>

          {/* Variant grid */}
          <div className="flex-1 overflow-y-auto px-[22px] py-[18px] pb-[26px]">
            {filteredCategories.length === 0 ? (
              <div className="py-10 text-center text-ink-5 text-[13px]">
                Nothing matches &ldquo;{search}&rdquo;.
              </div>
            ) : (
              filteredCategories.map((cat) => (
                <div key={cat.cat}>
                  <div className="text-[11px] font-bold tracking-[0.08em] uppercase text-ink-5 mb-3 mt-1.5 first:mt-0 [&:not(:first-child)]:mt-[26px]">
                    {cat.cat}
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {cat.variants.map((v) => (
                      <button
                        key={v.name}
                        onClick={() => handleInsert(v)}
                        className="group/card border border-ink-6 bg-paper rounded-xl px-[11px] py-2.5 text-left transition-all hover:border-tide hover:bg-tide-ss hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-8px_rgba(38,99,199,0.35)]"
                      >
                        <LiveThumb
                          sectionType={v.sectionType}
                          defaultProps={v.defaultProps}
                          thumbnailProps={v.thumbnailProps}
                        />
                        <b className="block text-[12px] text-ink-2 mt-2">
                          {v.name}
                        </b>
                        <span className="block text-[10px] text-ink-5 leading-[1.35] mt-px">
                          {v.description}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
